import { BRAND } from "@/config/brand";
import { useEffect } from "react";
import { useLocation } from "wouter";

type SeoConfig = {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: string;
};

const DEFAULT_OG_IMAGE = `${BRAND.siteUrl}/icons/icon-512.svg`;

const ROUTE_SEO: Array<{ match: (path: string) => boolean; seo: SeoConfig }> = [
  {
    match: (path) => path === "/",
    seo: {
      title: BRAND.title,
      description: BRAND.description,
    },
  },
  {
    match: (path) => path === "/login",
    seo: {
      title: `Login | ${BRAND.name}`,
      description:
        "Acesse sua conta MetaJuntos para gerenciar finanças, grupos e espaços financeiros compartilhados, powered by Guedes Investe.",
    },
  },
  {
    match: (path) => path === "/explore",
    seo: {
      title: `Explorar grupos | ${BRAND.name}`,
      description:
        "Descubra grupos públicos no MetaJuntos, acompanhe metas coletivas, rankings, progresso e participe de desafios financeiros com outras pessoas.",
      url: `${BRAND.siteUrl}/explore`,
    },
  },
  {
    match: (path) => path === "/dashboard",
    seo: {
      title: `Dashboard | ${BRAND.name}`,
      description:
        "Acompanhe seu painel de investimentos e evolução financeira no MetaJuntos.",
    },
  },
  {
    match: (path) => path === "/financial-v2",
    seo: {
      title: `Finanças | ${BRAND.name}`,
      description:
        "Dashboard financeiro premium do MetaJuntos para receitas, despesas, investimentos, rendimentos e saldo.",
    },
  },
  {
    match: (path) => path === "/investments",
    seo: {
      title: `Investimentos | ${BRAND.name}`,
      description: "Acompanhe posições, movimentações, distribuição e preços manuais da sua carteira pessoal.",
    },
  },
  {
    match: (path) => path === "/settings",
    seo: {
      title: `Configurações | ${BRAND.name}`,
      description:
        "Ajuste perfil, preferências e canais de feedback do beta fechado do MetaJuntos.",
    },
  },
  {
    match: (path) => path === "/feedback",
    seo: {
      title: `Feedback | ${BRAND.name}`,
      description:
        "Envie bugs, sugestões e melhorias para o beta fechado do MetaJuntos.",
    },
  },
  {
    match: (path) => path === "/groups" || path.startsWith("/groups/"),
    seo: {
      title: `Grupos de Investimento | ${BRAND.name}`,
      description:
        "Crie e acompanhe grupos de investimento no MetaJuntos com patrimônio, aportes, rendimentos e atividades.",
    },
  },
  {
    match: (path) =>
      path === "/financial-spaces" || path.startsWith("/financial-spaces/"),
    seo: {
      title: `Espaços Financeiros | ${BRAND.name}`,
      description:
        "Organize finanças compartilhadas no MetaJuntos com membros, permissões e lançamentos financeiros.",
    },
  },
];

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
}

function upsertLink(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLLinkElement>(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
}

export function setDocumentSeo(config: SeoConfig) {
  const type = config.type ?? "website";
  const image = config.image ?? DEFAULT_OG_IMAGE;
  const url = config.url ?? BRAND.siteUrl;

  document.title = config.title;
  upsertMeta('meta[name="description"]', {
    name: "description",
    content: config.description,
  });
  upsertMeta('meta[property="og:type"]', {
    property: "og:type",
    content: type,
  });
  upsertMeta('meta[property="og:title"]', {
    property: "og:title",
    content: config.title,
  });
  upsertMeta('meta[property="og:site_name"]', {
    property: "og:site_name",
    content: BRAND.name,
  });
  upsertMeta('meta[property="og:description"]', {
    property: "og:description",
    content: config.description,
  });
  upsertMeta('meta[property="og:url"]', {
    property: "og:url",
    content: url,
  });
  upsertMeta('meta[property="og:image"]', {
    property: "og:image",
    content: image,
  });
  upsertMeta('meta[property="og:image:alt"]', {
    property: "og:image:alt",
    content: config.title,
  });
  upsertMeta('meta[name="twitter:card"]', {
    name: "twitter:card",
    content: "summary_large_image",
  });
  upsertMeta('meta[name="twitter:title"]', {
    name: "twitter:title",
    content: config.title,
  });
  upsertMeta('meta[name="twitter:description"]', {
    name: "twitter:description",
    content: config.description,
  });
  upsertMeta('meta[name="twitter:image"]', {
    name: "twitter:image",
    content: image,
  });
  upsertLink('link[rel="canonical"]', {
    rel: "canonical",
    href: url,
  });
}

export default function Seo() {
  const [location] = useLocation();

  useEffect(() => {
    const routeSeo =
      ROUTE_SEO.find((item) => item.match(location))?.seo ?? {
        title: BRAND.name,
        description: BRAND.description,
      };
    const url = routeSeo.url ?? `${BRAND.siteUrl}${location === "/" ? "" : location}`;

    setDocumentSeo({
      ...routeSeo,
      image: DEFAULT_OG_IMAGE,
      url,
      type: "website",
    });
  }, [location]);

  return null;
}
